import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '@/styles/phone-input.css';
import {
  getPatient,
  updatePatient,
  updatePatientProfile,
  createPatientProfile,
} from '@/client/sdk.gen';
import type {
  PatientRead,
  PatientUpdate,
  PatientProfileCreate,
  Gender,
  CommunicationPreference,
  SmokingStatus,
} from '@/client/types.gen';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DashboardLayout from '@/app/dashboard/layout';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2,
  ArrowLeft,
  User,
  FileText,
  Heart,
  AlertCircle,
  Save,
} from 'lucide-react';

// Schema for basic patient information
const patientBasicSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  email: z.string().email('Invalid email address').optional().nullable(),
  phone: z.string().min(1, 'Phone number is required'),
  gender: z.enum(['male', 'female', 'prefer_not_to_say'] as const, {
    required_error: 'Please select a gender',
  }),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
});

// Schema for contact/emergency information
const patientContactSchema = z.object({
  emergency_contact_name: z.string().optional().nullable(),
  emergency_contact_phone: z.string().optional().nullable(),
  emergency_contact_relationship: z.string().optional().nullable(),
  preferred_pharmacy: z.string().optional().nullable(),
  preferred_language: z.string().optional().nullable(),
  preferred_communication: z
    .enum(['Email', 'Phone', 'SMS'] as const)
    .optional()
    .nullable(),
});

// Schema for insurance information
const patientInsuranceSchema = z.object({
  insurance_provider: z.string().optional().nullable(),
  insurance_policy_number: z.string().optional().nullable(),
});

// Schema for medical profile
const patientProfileSchema = z.object({
  medical_history: z.string().optional().nullable(),
  family_medical_history: z.string().optional().nullable(),
  social_history: z.string().optional().nullable(),
  occupation: z.string().optional().nullable(),
  smoking_status: z
    .enum(['Never', 'Former', 'Current', 'Unknown'] as const)
    .optional()
    .nullable(),
  alcohol_consumption: z.string().optional().nullable(),
  exercise_habits: z.string().optional().nullable(),
  diet_notes: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
});

// Combined schema types
type PatientBasicFormValues = z.infer<typeof patientBasicSchema>;
type PatientContactFormValues = z.infer<typeof patientContactSchema>;
type PatientInsuranceFormValues = z.infer<typeof patientInsuranceSchema>;
type PatientProfileFormValues = z.infer<typeof patientProfileSchema>;

export default function PatientEditPage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [patient, setPatient] = useState<PatientRead | null>(null);
  const [activeTab, setActiveTab] = useState('basic');

  // Forms for different sections
  const basicForm = useForm<PatientBasicFormValues>({
    resolver: zodResolver(patientBasicSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      gender: 'prefer_not_to_say',
      date_of_birth: '',
    },
  });

  const contactForm = useForm<PatientContactFormValues>({
    resolver: zodResolver(patientContactSchema),
    defaultValues: {
      emergency_contact_name: '',
      emergency_contact_phone: '',
      emergency_contact_relationship: '',
      preferred_pharmacy: '',
      preferred_language: '',
      preferred_communication: null,
    },
  });

  const insuranceForm = useForm<PatientInsuranceFormValues>({
    resolver: zodResolver(patientInsuranceSchema),
    defaultValues: {
      insurance_provider: '',
      insurance_policy_number: '',
    },
  });

  const profileForm = useForm<PatientProfileFormValues>({
    resolver: zodResolver(patientProfileSchema),
    defaultValues: {
      medical_history: '',
      family_medical_history: '',
      social_history: '',
      occupation: '',
      smoking_status: null,
      alcohol_consumption: '',
      exercise_habits: '',
      diet_notes: '',
      notes: '',
    },
  });

  // Fetch patient data
  useEffect(() => {
    if (!patientId) return;

    const fetchPatient = async () => {
      setIsLoading(true);
      try {
        const response = await getPatient({
          path: { patient_id: patientId },
        });

        if (response.data) {
          setPatient(response.data);

          // Set values for basic form
          basicForm.reset({
            name: response.data.name,
            email: response.data.email || '',
            phone: response.data.phone,
            gender: response.data.gender,
            date_of_birth: response.data.date_of_birth,
          });

          // Set values for contact form
          contactForm.reset({
            emergency_contact_name: response.data.emergency_contact_name || '',
            emergency_contact_phone:
              response.data.emergency_contact_phone || '',
            emergency_contact_relationship:
              response.data.emergency_contact_relationship || '',
            preferred_pharmacy: response.data.preferred_pharmacy || '',
            preferred_language: response.data.preferred_language || '',
            preferred_communication: response.data.preferred_communication,
          });

          // Set values for insurance form
          insuranceForm.reset({
            insurance_provider: response.data.insurance_provider || '',
            insurance_policy_number:
              response.data.insurance_policy_number || '',
          });

          // Set values for profile form if available
          if (response.data.profile) {
            profileForm.reset({
              medical_history: response.data.profile.medical_history || '',
              family_medical_history:
                response.data.profile.family_medical_history || '',
              social_history: response.data.profile.social_history || '',
              occupation: response.data.profile.occupation || '',
              smoking_status: response.data.profile.smoking_status,
              alcohol_consumption:
                response.data.profile.alcohol_consumption || '',
              exercise_habits: response.data.profile.exercise_habits || '',
              diet_notes: response.data.profile.diet_notes || '',
              notes: response.data.profile.notes || '',
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch patient:', error);
        toast({
          title: 'Error',
          description: 'Failed to load patient details',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [patientId, toast]);

  // Save all data at once
  const handleSaveAll = async () => {
    if (!patient || !patientId) return;

    // Validate all forms
    const basicValid = await basicForm.trigger();
    const contactValid = await contactForm.trigger();
    const insuranceValid = await insuranceForm.trigger();
    const profileValid = await profileForm.trigger();

    if (!basicValid || !contactValid || !insuranceValid || !profileValid) {
      // Find which tab has errors and switch to it
      if (!basicValid) setActiveTab('basic');
      else if (!contactValid) setActiveTab('contact');
      else if (!insuranceValid) setActiveTab('insurance');
      else setActiveTab('profile');

      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Prepare patient update data (combining basic, contact, and insurance forms)
      const patientUpdateData: PatientUpdate = {
        // Basic information
        name: basicForm.getValues().name,
        email: basicForm.getValues().email,
        phone: basicForm.getValues().phone,
        gender: basicForm.getValues().gender as Gender,
        date_of_birth: basicForm.getValues().date_of_birth,

        // Contact information
        emergency_contact_name: contactForm.getValues().emergency_contact_name,
        emergency_contact_phone:
          contactForm.getValues().emergency_contact_phone,
        emergency_contact_relationship:
          contactForm.getValues().emergency_contact_relationship,
        preferred_pharmacy: contactForm.getValues().preferred_pharmacy,
        preferred_language: contactForm.getValues().preferred_language,
        preferred_communication: contactForm.getValues()
          .preferred_communication as CommunicationPreference,

        // Insurance information
        insurance_provider: insuranceForm.getValues().insurance_provider,
        insurance_policy_number:
          insuranceForm.getValues().insurance_policy_number,
      };

      // Prepare profile update data
      const profileData: PatientProfileCreate = {
        medical_history: profileForm.getValues().medical_history,
        family_medical_history: profileForm.getValues().family_medical_history,
        social_history: profileForm.getValues().social_history,
        occupation: profileForm.getValues().occupation,
        smoking_status: profileForm.getValues().smoking_status as SmokingStatus,
        alcohol_consumption: profileForm.getValues().alcohol_consumption,
        exercise_habits: profileForm.getValues().exercise_habits,
        diet_notes: profileForm.getValues().diet_notes,
        notes: profileForm.getValues().notes,
      };

      // Update patient basic data
      await updatePatient({
        path: { patient_id: patientId },
        body: patientUpdateData,
      });

      // Update or create patient profile
      if (patient.profile) {
        await updatePatientProfile({
          path: { patient_id: patientId },
          body: profileData,
        });
      } else {
        await createPatientProfile({
          path: { patient_id: patientId },
          body: profileData,
        });
      }

      toast({
        title: 'Success',
        description: 'Patient information updated successfully',
      });

      // Refetch patient data to get updated information
      const response = await getPatient({
        path: { patient_id: patientId },
      });

      if (response.data) {
        setPatient(response.data);
      }
    } catch (error) {
      console.error('Failed to update patient:', error);
      toast({
        title: 'Error',
        description: 'Failed to update patient information',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle going back to patients list
  const handleBack = () => {
    navigate('/dashboard/patients');
  };

  if (isLoading) {
    return (
      <DashboardLayout
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Patients', href: '/dashboard/patients' },
          {
            label: 'Edit Patient',
            href: `/dashboard/patients/${patientId}/edit`,
          },
        ]}
      >
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2">Loading patient data...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      breadcrumbs={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Patients', href: '/dashboard/patients' },
        {
          label: 'Edit Patient',
          href: `/dashboard/patients/${patientId}/edit`,
        },
      ]}
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Edit Patient: {patient?.name}
              </h1>
              <p className="text-muted-foreground">
                Update information for this patient
              </p>
            </div>
          </div>
          <Button onClick={handleSaveAll} disabled={isSaving} className="gap-2">
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save All Changes
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Patient Information</CardTitle>
            <CardDescription>
              Edit this patient's information using the tabs below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="grid grid-cols-4 mb-8">
                <TabsTrigger value="basic" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>Basic Info</span>
                </TabsTrigger>
                <TabsTrigger
                  value="contact"
                  className="flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  <span>Contact</span>
                </TabsTrigger>
                <TabsTrigger
                  value="insurance"
                  className="flex items-center gap-2"
                >
                  <AlertCircle className="h-4 w-4" />
                  <span>Insurance</span>
                </TabsTrigger>
                <TabsTrigger
                  value="profile"
                  className="flex items-center gap-2"
                >
                  <Heart className="h-4 w-4" />
                  <span>Medical Profile</span>
                </TabsTrigger>
              </TabsList>

              {/* Basic Information Tab */}
              <TabsContent value="basic">
                <Form {...basicForm}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={basicForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Patient Name *</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={basicForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              {...field}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={basicForm.control}
                      name="phone"
                      render={() => (
                        <FormItem>
                          <FormLabel>Phone Number *</FormLabel>
                          <FormControl>
                            <Controller
                              control={basicForm.control}
                              name="phone"
                              render={({ field: { value, onChange } }) => (
                                <PhoneInput
                                  international
                                  defaultCountry="IN"
                                  value={value}
                                  onChange={onChange}
                                  className="phone-input-dark"
                                />
                              )}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={basicForm.control}
                      name="date_of_birth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date of Birth *</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={basicForm.control}
                      name="gender"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Gender *</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select gender" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="prefer_not_to_say">
                                Prefer not to say
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </TabsContent>

              {/* Contact Information Tab */}
              <TabsContent value="contact">
                <Form {...contactForm}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={contactForm.control}
                      name="emergency_contact_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Emergency Contact Name</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="emergency_contact_phone"
                      render={() => (
                        <FormItem>
                          <FormLabel>Emergency Contact Phone</FormLabel>
                          <FormControl>
                            <Controller
                              control={contactForm.control}
                              name="emergency_contact_phone"
                              render={({ field: { value, onChange } }) => (
                                <PhoneInput
                                  international
                                  defaultCountry="IN"
                                  value={value || ''}
                                  onChange={onChange}
                                  className="phone-input-dark"
                                />
                              )}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="emergency_contact_relationship"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Relationship to Patient</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="preferred_pharmacy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Pharmacy</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="preferred_language"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Language</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={contactForm.control}
                      name="preferred_communication"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Communication Method</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select communication method" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Email">Email</SelectItem>
                              <SelectItem value="Phone">Phone</SelectItem>
                              <SelectItem value="SMS">SMS</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </TabsContent>

              {/* Insurance Information Tab */}
              <TabsContent value="insurance">
                <Form {...insuranceForm}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={insuranceForm.control}
                      name="insurance_provider"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Insurance Provider</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={insuranceForm.control}
                      name="insurance_policy_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Policy Number</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </TabsContent>

              {/* Medical Profile Tab */}
              <TabsContent value="profile">
                <Form {...profileForm}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={profileForm.control}
                      name="medical_history"
                      render={({ field }) => (
                        <FormItem className="col-span-full">
                          <FormLabel>Medical History</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={3}
                              {...field}
                              value={field.value || ''}
                              placeholder="Past medical conditions, surgeries, etc."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="family_medical_history"
                      render={({ field }) => (
                        <FormItem className="col-span-full">
                          <FormLabel>Family Medical History</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={3}
                              {...field}
                              value={field.value || ''}
                              placeholder="Relevant family medical conditions"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="occupation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Occupation</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ''} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="smoking_status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Smoking Status</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value || undefined}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Never">Never</SelectItem>
                              <SelectItem value="Former">Former</SelectItem>
                              <SelectItem value="Current">Current</SelectItem>
                              <SelectItem value="Unknown">Unknown</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="alcohol_consumption"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Alcohol Consumption</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="E.g., Occasional, Weekly, Daily, None"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="exercise_habits"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Exercise Habits</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              value={field.value || ''}
                              placeholder="E.g., Sedentary, Moderate, Active"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="diet_notes"
                      render={({ field }) => (
                        <FormItem className="col-span-full">
                          <FormLabel>Diet Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={2}
                              {...field}
                              value={field.value || ''}
                              placeholder="Dietary preferences, restrictions, etc."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="social_history"
                      render={({ field }) => (
                        <FormItem className="col-span-full">
                          <FormLabel>Social History</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={2}
                              {...field}
                              value={field.value || ''}
                              placeholder="Relevant social factors affecting health"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={profileForm.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem className="col-span-full">
                          <FormLabel>Additional Notes</FormLabel>
                          <FormControl>
                            <Textarea
                              rows={3}
                              {...field}
                              value={field.value || ''}
                              placeholder="Any other relevant information"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </Form>
              </TabsContent>
            </Tabs>
          </CardContent>
          <CardFooter className="flex justify-between border-t p-6">
            <Button variant="outline" onClick={handleBack}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={isSaving}
              className="gap-2"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save All Changes
            </Button>
          </CardFooter>
        </Card>
      </div>
    </DashboardLayout>
  );
}
